import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';

const AdminServiceRequestsPage = () => {
  const { token, user: currentUser, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [serviceRequests, setServiceRequests] = useState([]);
  const [users, setUsers] = useState([]); // For technician dropdown
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Fetch all service requests
  const fetchServiceRequests = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    setError('');
    try {
      console.log('[AdminServiceRequests] Fetching with token:', token ? 'Present' : 'Missing');
      
      // Try the admin endpoint first, fall back to general endpoint
      const endpoints = ['/api/service-requests/admin', '/api/service-requests'];
      let response;
      let lastError;
      
             for (const endpoint of endpoints) {
         try {
           console.log(`[AdminServiceRequests] Trying endpoint: ${endpoint}`);
           const query = new URLSearchParams({
             _t: Date.now().toString(),
             limit: '100'
           });
           response = await fetch(`${endpoint}?${query.toString()}`, {
              method: 'GET',
              headers: { 
                'Authorization': `Bearer ${token}`,
               'Content-Type': 'application/json',
               'Cache-Control': 'no-cache, no-store, must-revalidate',
               'Pragma': 'no-cache',
               'Expires': '0'
              },
              credentials: 'include'
            });
          
          if (response.ok) {
            console.log(`[AdminServiceRequests] Success with endpoint: ${endpoint}`);
            break;
          } else {
            console.log(`[AdminServiceRequests] Failed with endpoint: ${endpoint}, status: ${response.status}`);
            lastError = await response.json();
          }
        } catch (err) {
          console.log(`[AdminServiceRequests] Error with endpoint: ${endpoint}`, err);
          lastError = err;
          continue;
        }
      }
      
      if (!response || !response.ok) {
        throw new Error(lastError?.message || 'Failed to fetch service requests from any endpoint');
      }
      
      console.log('[AdminServiceRequests] Response status:', response.status);
      const data = await response.json();
      console.log('[AdminServiceRequests] Data received:', data);
      
      // Handle different response formats
      let requests = [];
      if (data.status === 'success' && data.data && data.data.serviceRequests) {
        requests = data.data.serviceRequests;
      } else if (data.status === 'success' && data.data && Array.isArray(data.data)) {
        requests = data.data;
      } else if (Array.isArray(data)) {
        requests = data;
      } else if (data.serviceRequests && Array.isArray(data.serviceRequests)) {
        requests = data.serviceRequests;
      } else {
        console.warn('[AdminServiceRequests] Unexpected data structure:', data);
        requests = [];
      }
      
      console.log('[AdminServiceRequests] Processed requests:', requests);
      setServiceRequests(requests);
      
    } catch (err) {
      setError(err.message);
      console.error("Error fetching service requests:", err);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  // Fetch users for technician assignment
  const fetchUsers = useCallback(async () => {
    if (!token) return;
    try {
      const response = await fetch('/api/users', {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Cache-Control': 'no-cache'
        },
      });
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Failed to fetch users');
      }
      const data = await response.json();
      // Filter for users who can be technicians (e.g., admin, manager, staff)
      const potentialTechnicians = Array.isArray(data?.data?.users) 
        ? data.data.users.filter(u => u && u.role && ['admin', 'manager', 'staff'].includes(u.role)) 
        : [];
      setUsers(potentialTechnicians);
    } catch (err) {
      console.error("Error fetching users:", err);
      // Don't set error for users fetch failure, just log it
    }
  }, [token]);

  useEffect(() => {
    if (!authLoading && (!currentUser || (currentUser.role !== 'admin' && currentUser.role !== 'manager'))) {
      console.log('[AdminServiceRequests] Access denied, redirecting to login');
      navigate('/login');
    } else if (token && currentUser) {
      console.log('[AdminServiceRequests] Authorized user, fetching data');
      fetchServiceRequests();
      fetchUsers();
    }
  }, [authLoading, currentUser, token, navigate, fetchServiceRequests, fetchUsers]);

  const handleUpdateRequest = async (requestId, updateData) => {
    if (!token || !requestId) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`/api/service-requests/${requestId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(updateData),
      });
      
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.message || 'Failed to update service request');
      }
      
      // Refresh data
      await fetchServiceRequests(); 
      console.log('Service request updated successfully');
      
    } catch (err) {
      setError(err.message);
      console.error('Error updating service request:', err);
      alert(`Error: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = (requestId, newStatus) => {
    if (!requestId || !newStatus) return;
    handleUpdateRequest(requestId, { status: newStatus });
  };

  const handleAssignTechnician = (requestId, technicianId) => {
    if (!requestId) return;
    // technicianId could be an empty string or null to unassign
    handleUpdateRequest(requestId, { assignedTo: technicianId || null });
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString() + ' ' + new Date(dateString).toLocaleTimeString();
    } catch (e) {
      return 'Invalid Date';
    }
  };

  if (authLoading) {
    return <div className="container mx-auto p-4"><p>Checking authentication...</p></div>;
  }

  if (!currentUser || (currentUser.role !== 'admin' && currentUser.role !== 'manager')) {
    return <div className="container mx-auto p-4"><p>Access denied. Admin or Manager role required.</p></div>;
  }

  if (isLoading && serviceRequests.length === 0) {
    return <div className="container mx-auto p-4"><p>Loading service requests...</p></div>;
  }

  return (
    <div className="container mx-auto p-4 md:p-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-brand-text-dark">Manage Service Requests</h1>
        <button 
          onClick={fetchServiceRequests}
          disabled={isLoading}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
        >
          {isLoading ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          <strong>Error:</strong> {error}
        </div>
      )}

      <div className="bg-white shadow-md rounded-lg overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Request ID</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Service Type</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requested</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned To</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {serviceRequests.length > 0 ? serviceRequests.map((req) => (
              <tr key={req.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  <Link to={`/admin/service-requests/${req.id}`} className="text-brand-accent-orange hover:underline">
                    #{req.id}
                  </Link>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {req.customer ? `${req.customer.username} (${req.customer.email})` : (req.contactFullName || 'Guest')}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{req.serviceType || 'N/A'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{formatDate(req.createdAt)}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <select 
                    value={req.status || 'pending'}
                    onChange={(e) => handleStatusChange(req.id, e.target.value)}
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-brand-accent-orange focus:border-brand-accent-orange sm:text-sm rounded-md"
                    disabled={isLoading}
                  >
                    {['pending', 'confirmed', 'in_progress', 'completed', 'cancelled'].map(status => (
                      <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1).replace('_',' ')}</option>
                    ))}
                  </select>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <select
                    value={req.assignedTo || ''} // Handle null assignedTo
                    onChange={(e) => handleAssignTechnician(req.id, e.target.value)}
                    className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-brand-accent-orange focus:border-brand-accent-orange sm:text-sm rounded-md"
                    disabled={isLoading}
                  >
                    <option value="">Unassigned</option>
                    {users.map(tech => (
                      <option key={tech.id} value={tech.id}>{tech.username} ({tech.role})</option>
                    ))}
                  </select>
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                  {isLoading ? 'Loading...' : 'No service requests found.'}
                  {!isLoading && (
                    <div className="mt-2">
                      <button 
                        onClick={fetchServiceRequests}
                        className="text-blue-600 hover:text-blue-800 underline text-sm"
                      >
                        Try refreshing the data
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Debug information (remove in production) */}
      {process.env.NODE_ENV === 'development' && (
        <div className="mt-4 p-4 bg-gray-100 rounded text-sm">
          <strong>Debug Info:</strong>
          <ul className="mt-2">
            <li>Current User: {currentUser ? `${currentUser.username} (${currentUser.role})` : 'None'}</li>
            <li>Token: {token ? 'Present' : 'Missing'}</li>
            <li>Service Requests Count: {serviceRequests.length}</li>
            <li>Users Count: {users.length}</li>
            <li>Loading: {isLoading ? 'Yes' : 'No'}</li>
            <li>Error: {error || 'None'}</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default AdminServiceRequestsPage;